/**
 * The Memory routes (slice 1d spec §7, contracts §9.1) and their query keys.
 * `['memory']` is the file list, `['memory', 'file', id]` one file with its
 * content; a `memory` hub event invalidates both (C§10), so an open file
 * refetches and the editor decides whether to take it (`memory-view.ts`).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { api, ApiError } from "../../app/api";
import { fresher } from "../../app/draft";

/** Who changed a file last: `person:<user_id>` or `agent:<agent_id>`. */
export type UpdatedBy = `person:${string}` | `agent:${string}`;

/** A file's metadata, as `GET /api/memory` lists it: no content. */
export type MemoryFileMeta = {
  id: string;
  name: string;
  /** Null for the workspace's files (`USER.md`, `VOICE.md`, `CREW.md`). */
  agent_id: string | null;
  length: number;
  version: number;
  updated_by: UpdatedBy;
  updated_at: string;
};

export type MemoryFile = MemoryFileMeta & { content: string };

export const memoryKey = ["memory"] as const;
export const memoryFileKey = (id: string) => ["memory", "file", id] as const;

/** The caps, and what's left of the every-run total (§3.4). */
export type Budget = { file_max: number; every_run_max: number; every_run_left: number };

export type MemoryList = { files: MemoryFileMeta[]; budget?: Budget };

/**
 * The list's query. A fetch that started before a save and lands after it
 * keeps each row's newer version (`fresher`), whatever order they arrive in.
 */
export const memoryListQuery = {
  queryKey: memoryKey,
  queryFn: async (): Promise<MemoryList> => {
    const { files, budget } = await api<MemoryList>("GET", "/api/memory");
    return { files, budget };
  },
  structuralSharing: (old: unknown, next: unknown): MemoryList => {
    const list = next as MemoryList;
    const kept = new Map((old as MemoryList | undefined)?.files.map((f) => [f.id, f]));
    return { ...list, files: list.files.map((f) => fresher(kept.get(f.id), f)) };
  },
};

/** One file's query; a fetch never replaces a newer cached version (`fresher`). */
export const memoryFileQuery = (id: string) => ({
  queryKey: memoryFileKey(id),
  queryFn: async () => (await api<{ file: MemoryFile }>("GET", `/api/memory/files/${id}`)).file,
  structuralSharing: (old: unknown, next: unknown): MemoryFile =>
    fresher(old as MemoryFile | undefined, next as MemoryFile),
});

export function useMemoryFiles() {
  return useQuery(memoryListQuery);
}

export function useMemoryFile(id: string | undefined) {
  return useQuery({
    ...memoryFileQuery(id ?? ""),
    enabled: Boolean(id),
    // Hub events keep it fresh; a refetch while editing only raises "Reload?".
    staleTime: 5_000,
  });
}

/**
 * What a refused save carries (§3.4, §4.4): `409 conflict` has the current
 * file, `400 over_budget` how many characters the every-run files have left.
 */
export type SaveRefusal =
  | { kind: "conflict"; file: MemoryFile; hint: string }
  | { kind: "over_budget"; left: number | null; hint: string }
  | { kind: "too_long"; hint: string }
  | { kind: "other"; hint: string };

export function refusalOf(error: unknown): SaveRefusal | null {
  if (!(error instanceof ApiError)) return null;
  const { code, hint, body } = error;
  if (code === "conflict" && isFile(body.file)) return { kind: "conflict", file: body.file, hint };
  if (code === "over_budget") {
    return { kind: "over_budget", left: typeof body.left === "number" ? body.left : null, hint };
  }
  if (code === "too_long") return { kind: "too_long", hint };
  return { kind: "other", hint };
}

function isFile(v: unknown): v is MemoryFile {
  const f = v as MemoryFile | null;
  return typeof f?.id === "string" && typeof f.content === "string" && Number.isInteger(f.version);
}

/**
 * Puts a copy of the file into the cache (unless it holds a newer one), and
 * its metadata into the list; the list refetches for its new budget.
 */
function useStoreFile() {
  const client = useQueryClient();
  return (file: MemoryFile) => {
    const kept = client.setQueryData<MemoryFile>(memoryFileKey(file.id), (old) =>
      fresher(old, file),
    );
    client.setQueryData<MemoryList>(memoryKey, (old) =>
      old && kept
        ? { ...old, files: old.files.map((f) => (f.id === kept.id ? metaOf(kept) : f)) }
        : old,
    );
    void client.invalidateQueries({ queryKey: memoryKey, exact: true });
  };
}

function metaOf({ content: _content, ...meta }: MemoryFile): MemoryFileMeta {
  return meta;
}

/** `PUT /api/memory/files/:id` from `version`; a stale version is `409 conflict`. */
export function useSaveFile(id: string) {
  const store = useStoreFile();
  return useMutation({
    mutationFn: async (edit: { content: string; version: number }) =>
      (await api<{ file: MemoryFile }>("PUT", `/api/memory/files/${id}`, edit)).file,
    onSuccess: store,
    onError: storeConflict(store),
  });
}

/**
 * A conflict's current file is the file now: "Use theirs", and a later save
 * or undo, start from it.
 */
function storeConflict(store: (file: MemoryFile) => void) {
  return (error: Error) => {
    const refused = refusalOf(error);
    if (refused?.kind === "conflict") store(refused.file);
  };
}

/**
 * `POST /api/memory/files/:id/undo` with the version shown: the change that
 * made it is undone, as a new version. `409 conflict` (with the current file)
 * when a newer version has come since, so it's never undone unseen.
 */
export function useUndoFile(id: string) {
  const store = useStoreFile();
  return useMutation({
    mutationFn: async (version: number) =>
      (await api<{ file: MemoryFile }>("POST", `/api/memory/files/${id}/undo`, { version })).file,
    onSuccess: store,
    onError: storeConflict(store),
  });
}

/**
 * A day's note (slice 1m spec §2, §6): what the agent noted that day, a line
 * each (`- 14:05 …`). `updated_by` is `agent:<slug>`, `person:<user_id>`, or
 * `import` (from the old computer, at a move).
 */
export type MemoryNote = {
  id: string;
  /** `YYYY-MM-DD`, the workspace's day. */
  day: string;
  content: string;
  length: number;
  limit: number;
  version: number;
  updated_by: string;
  updated_at: string;
};

type NotesPage = { notes: MemoryNote[]; more: boolean };

/** One agent's notes, every page; under `['memory']`, so a `memory` hub event refetches them. */
export const memoryNotesKey = (agentId: string) => ["memory", "notes", agentId] as const;

/** The days the page shows at once, and each "Show older days". */
const NOTES_PAGE = 14;

/** `GET /api/memory/notes`, newest day first; each next page is the days before the last one shown. */
export function useMemoryNotes(agentId: string) {
  return useInfiniteQuery({
    queryKey: memoryNotesKey(agentId),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ agent_id: agentId, limit: String(NOTES_PAGE) });
      if (pageParam) params.set("before", pageParam);
      return api<NotesPage>("GET", `/api/memory/notes?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.more ? (last.notes.at(-1)?.day ?? null) : null),
  });
}

/** Every page's days, newest first, each day once (a refetch can shift a page's edge). */
export function notesOf(pages: NotesPage[] | undefined): MemoryNote[] {
  const seen = new Set<string>();
  return (pages ?? [])
    .flatMap((p) => p.notes)
    .filter((n) => !seen.has(n.id) && Boolean(seen.add(n.id)));
}

/** A refused note save: `409 conflict` carries the current note. */
export function noteConflictOf(error: unknown): MemoryNote | null {
  if (!(error instanceof ApiError) || error.code !== "conflict") return null;
  const n = error.body.note as MemoryNote | null | undefined;
  return typeof n?.id === "string" && typeof n.content === "string" && Number.isInteger(n.version)
    ? n
    : null;
}

/** Puts a day's note into its agent's pages, in place, unless the cache holds a newer one. */
function useStoreNote(agentId: string) {
  const client = useQueryClient();
  return (note: MemoryNote) => {
    client.setQueryData<InfiniteData<NotesPage>>(memoryNotesKey(agentId), (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              notes: p.notes.map((n) => (n.id === note.id ? fresher(n, note) : n)),
            })),
          }
        : old,
    );
  };
}

/** `PUT /api/memory/notes/:id` from `version` (owner); a stale version is `409 conflict` with the note now. */
export function useSaveNote(agentId: string, id: string) {
  const store = useStoreNote(agentId);
  return useMutation({
    mutationFn: async (edit: { content: string; version: number }) =>
      (await api<{ note: MemoryNote }>("PUT", `/api/memory/notes/${id}`, edit)).note,
    onSuccess: store,
    onError: (error) => {
      const current = noteConflictOf(error);
      if (current) store(current);
    },
  });
}

/**
 * `DELETE /api/memory/notes/:id` (owner): the day is forgotten, and leaves the
 * list at once. A list fetch begun before it is cancelled, and the list is
 * fetched again after, so an older answer can't bring the day back.
 */
export function useForgetNote(agentId: string, id: string) {
  const client = useQueryClient();
  const queryKey = memoryNotesKey(agentId);
  return useMutation({
    mutationFn: () => api<{ ok: true }>("DELETE", `/api/memory/notes/${id}`),
    onMutate: () => client.cancelQueries({ queryKey }),
    onSettled: () => client.invalidateQueries({ queryKey }),
    onSuccess: () => {
      client.setQueryData<InfiniteData<NotesPage>>(queryKey, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p) => ({ ...p, notes: p.notes.filter((n) => n.id !== id) })),
            }
          : old,
      );
    },
  });
}
